import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { InvoiceTemplate } from '../../../api/dto/invoice-delivery.dto'
import { useGetDeliveryClientsQuery } from '../../../api/invoice-delivery-api-client'
import { getDefaultOe1DueDate } from './invoiceTemplates'
import { isOe1Incomplete, RowEdits } from './reviewValues'
import {
  buildReviewRows,
  ReviewFile,
  toUploadChoice,
  UploadChoice,
} from './reviewRows'
import { ParsedInvoiceSheet, parseInvoiceSheet } from './uploadReview'

/** State of the upload review: templates, typed values and removed files. */
export const useUploadReview = (
  files: ReviewFile[],
  defaultTemplate: InvoiceTemplate,
  billingPeriodStart: string
) => {
  // Only templates picked in the review; the rest are guessed from the sheet.
  const [templateByKey, setTemplateByKey] = useState<
    Record<string, InvoiceTemplate>
  >({})
  const [editsByKey, setEditsByKey] = useState<Record<string, RowEdits>>({})
  const [chosenClientByKey, setChosenClientByKey] = useState<
    Record<string, string>
  >({})
  const [parsedByKey, setParsedByKey] = useState<
    Record<string, ParsedInvoiceSheet>
  >({})
  const [removedKeys, setRemovedKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const [oe1DueDate, setOe1DueDate] = useState<string | null>(() =>
    getDefaultOe1DueDate(dayjs())
  )
  const { data: clientData, isLoading: isClientsLoading } =
    useGetDeliveryClientsQuery(billingPeriodStart)
  const clients = useMemo(() => clientData?.clients ?? [], [clientData])

  useEffect(() => {
    let isCancelled = false
    Promise.all(
      files.map(async ({ key, file }) => {
        const parsed = await parseInvoiceSheet(file).catch(() => ({
          companyName: null,
          totalDue: null,
        }))
        return [key, parsed] as const
      })
    ).then((entries) => {
      if (!isCancelled) setParsedByKey(Object.fromEntries(entries))
    })
    return () => {
      isCancelled = true
    }
  }, [files])

  const rows = useMemo(
    () =>
      buildReviewRows({
        files,
        removedKeys,
        parsedByKey,
        templateByKey,
        defaultTemplate,
        editsByKey,
        chosenClientByKey,
        clients,
        oe1DueDate,
      }),
    [
      files,
      removedKeys,
      parsedByKey,
      templateByKey,
      defaultTemplate,
      editsByKey,
      chosenClientByKey,
      clients,
      oe1DueDate,
    ]
  )

  // A typed value belongs to the template it was typed for.
  const setTemplate = (key: string, template: InvoiceTemplate) => {
    setTemplateByKey((prev) => ({ ...prev, [key]: template }))
    setEditsByKey(({ [key]: _dropped, ...rest }) => rest)
  }
  const setAllTemplates = (template: InvoiceTemplate) => {
    setTemplateByKey(Object.fromEntries(files.map((f) => [f.key, template])))
    setEditsByKey({})
  }
  const editRow = (key: string, edits: RowEdits) =>
    setEditsByKey((prev) => ({ ...prev, [key]: { ...prev[key], ...edits } }))
  const chooseClient = (key: string, clientId: string) =>
    setChosenClientByKey((prev) => ({ ...prev, [key]: clientId }))
  const removeFile = (key: string) =>
    setRemovedKeys((prev) => new Set(prev).add(key))

  const choices: Record<string, UploadChoice> = Object.fromEntries(
    rows.map((row) => [row.key, toUploadChoice(row)])
  )

  return {
    rows,
    choices,
    clients,
    oe1DueDate,
    setOe1DueDate,
    setTemplate,
    setAllTemplates,
    editRow,
    chooseClient,
    removeFile,
    hasOe1: rows.some((row) => row.template === InvoiceTemplate.OE_FIRST),
    hasDuplicates: rows.some((row) => row.isDuplicate),
    isOe1Incomplete: rows.some((row) =>
      isOe1Incomplete(row.template, row.values)
    ),
    isReady:
      !isClientsLoading && Object.keys(parsedByKey).length === files.length,
  }
}
